import { JobDescriptionUtils } from '@/utils/job-description-util/job-description.util';
import Showdown from 'showdown';
const showdownConverter = new Showdown.Converter();
/**
 * @description The function removes tags from an HTML string with the
 * posibility of removing or not the inner contents of the specified tags.
 * The function uses a basic dfs for its aproach https://www.geeksforgeeks.org/depth-first-search-or-dfs-for-a-graph/
 * to traverse the DOM tree.
 * @param {String} htmlString
 * @param {String} removingTag - a Element.tagName string https://developer.mozilla.org/en-US/docs/Web/API/Element/tagName
 * @param {Boolean} removesInnerContent
 * @returns {String} a parsed html string without the specified tags
 */
export const removeTagsFromHTMLString = (
  htmlString: string,
  removingTag: string,
  removesInnerContent = false,
): string => {
  if (!htmlString) {
    return '';
  }

  let parsedHTML = htmlString;

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);

    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    const removingTagName = removingTag.toUpperCase();

    if (Array.isArray(nodesArray) && nodesArray.length) {
      _traverseAndRemoveTagsFromNodeArray(nodesArray, removingTagName, removesInnerContent);
    }

    parsedHTML = _getHTMLStringFromDocumentFragment(fragment);
  }

  return parsedHTML;
};

/**
 * Finds all HTML tags of a specified type that contain content matching a given pattern.
 * The function recursively traverses the DOM tree to find matching tags at any depth.
 *
 * @param {string} htmlString - The HTML string to search through
 * @param {string} findingTag - The HTML tag name to look for (e.g. 'P', 'DIV', etc)
 * @param {RegExp} content - Regular expression pattern to match against the tag's inner content
 * @returns {string[]} Array of HTML strings containing the matching tags with their content
 *
 * @example
 * // Find all <p> tags that contain only a bullet point
 * findTagsWithSpecifiedContentOnHTMLString(
 *   '<p>Text</p><p>•</p><p>More text</p>',
 *   'P',
 *   /^(\s)*[•](\s)*$/
 * )
 * // Returns: ['<p>•</p>']
 */
export const findTagsWithSpecifiedContentOnHTMLString = (
  htmlString: string,
  findingTag: string,
  content: RegExp,
): string[] => {
  if (!htmlString) {
    return [];
  }

  let tags: string[] = [];

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);
    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    if (Array.isArray(nodesArray) && nodesArray.length) {
      for (const node of nodesArray) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.nodeName === findingTag) {
            if (content.test(element.innerHTML)) {
              tags.push(element.outerHTML);
            }
          }
        }

        if (node.childNodes) {
          for (const childNode of node.childNodes) {
            if (childNode.nodeType === Node.ELEMENT_NODE) {
              const childElement = childNode as HTMLElement;
              tags = [
                ...tags,
                ...findTagsWithSpecifiedContentOnHTMLString(
                  childElement.outerHTML,
                  findingTag,
                  content,
                ),
              ];
            }
          }
        }
      }
    }
  }

  return tags;
};

/**
 * Removes all HTML tags of a specified type that contain content matching a given pattern.
 * @param htmlString - The HTML string to search through
 * @param findingTag - The HTML tag name to look for (e.g. 'P', 'DIV', etc)
 * @param matchRegex - Regular expression pattern to match against the tag's inner content
 * @returns {string} A parsed HTML string without the specified tags
 *
 * @example
 * // Remove all <p> tags that contain only a bullet point
 * removeTagsWithSpecifiedContentOnHTMLString(
 *   '<p>Text</p><p>•</p><p>More text</p>',
 *   'P',
 *   /^\s*•\s*$/,
 * )
 * // Returns: '<p>Text</p><p>More text</p>'
 */
export const removeTagsWithSpecifiedContentOnHTMLString = (
  htmlString: string,
  findingTag: string,
  matchRegex: RegExp,
): string => {
  if (!htmlString) {
    return '';
  }

  let parsedHTML = htmlString;

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);
    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    if (Array.isArray(nodesArray) && nodesArray.length) {
      for (const node of nodesArray) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.nodeName === findingTag) {
            if (matchRegex.test(element.innerHTML)) {
              element.parentNode?.removeChild(element);
            }
          }
        }
      }
    }

    parsedHTML = _getHTMLStringFromDocumentFragment(fragment);
  }

  return parsedHTML;
};

/**
 * Finds all HTML tags of a specified type that contain content matching a given pattern and
 * replaces their content with the specified content.
 * The function recursively traverses the DOM tree to find matching tags at any depth.
 *
 * @param {string} htmlString - The HTML string to search through
 * @param {string} findingTag - The HTML tag name to look for (e.g. 'P', 'DIV', etc)
 * @param {RegExp} content - Regular expression pattern to match against the tag's inner content
 * @returns {string[]} Array of HTML strings containing the matching tags with their content
 *
 * @example
 * // Find all <p> tags that contain only a bullet point and replace them with <b> tags
 * replaceTagsWithSpecifiedContentOnHTMLStringWithSpecifiedContent(
 *   '<p>Text</p><p>•</p><p>More text</p>',
 *   'P',
 *   /^(\s)*[•](\s)*$/,
 *   '<b>$1</b>'
 * )
 * // Returns: <p>Text</p><p><b>•</b></p><p>More text</p>
 */
export const replaceTagsWithSpecifiedContentOnHTMLStringWithSpecifiedContent = (
  htmlString: string,
  findingTag: string,
  content: RegExp,
  replaceFunction: (content: string) => string,
): string => {
  if (!htmlString) {
    return '';
  }

  let parsedHTML = htmlString;

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);
    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    if (Array.isArray(nodesArray) && nodesArray.length) {
      for (const node of nodesArray) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.nodeName === findingTag) {
            if (content.test(element.innerHTML)) {
              element.innerHTML = replaceFunction(element.innerHTML);
            }
          }
        }

        if (node.childNodes) {
          for (const childNode of node.childNodes) {
            if (childNode.nodeType === Node.ELEMENT_NODE) {
              const childElement = childNode as HTMLElement;
              const fixedChildHTML =
                replaceTagsWithSpecifiedContentOnHTMLStringWithSpecifiedContent(
                  childElement.outerHTML,
                  findingTag,
                  content,
                  replaceFunction,
                );

              childElement.outerHTML = fixedChildHTML;
            }
          }
        }
      }
    }

    parsedHTML = _getHTMLStringFromDocumentFragment(fragment);
  }

  return parsedHTML;
};

/**
 * Finds all HTML tags of a specified type that contain content matching a given pattern.
 * The function recursively traverses the DOM tree to find matching tags at any depth.
 *
 * @param {string} htmlString - The HTML string to search through
 * @param {string} findingTag - The HTML tag name to look for (e.g. 'P', 'DIV', etc)
 * @param {string} openingSeparator - The opening separator to look for
 * @param {string} closingSeparator - The closing separator to look for
 * @returns {string[][]} Array of pairs of HTML strings containing the matching tags with their content
 *
 * @example
 * Find all the p tags that should be bolded using ** separators even if on multiple lines
 * findTagsBrokenIntoMultipleLinesWithOpeningAndClosingSeparators(
 *   '<p>**Text</p><p>**</p><p>More text</p>',
 *   'P',
 *   '**',
 *   '**'
 * )
 * // Returns: [['<p>**Text</p>', '<p>**</p>']]
 */
export const findTagsBrokenIntoMultipleLinesWithOpeningAndClosingSeparators = (
  htmlString: string,
  findingTag: string,
  openingSeparator: RegExp,
  closingSeparator: RegExp,
): string[][] => {
  let tags: string[][] = [];

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);
    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    let pairs: string[] = [];
    let isLookingForClosingSeparator = false;
    let currentPairNodeSeparationCount = 0;

    if (Array.isArray(nodesArray) && nodesArray.length) {
      for (const node of nodesArray) {
        currentPairNodeSeparationCount++;

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.nodeName === findingTag) {
            // Opening and closing separators should be siblings for the tag to be bolded
            if (!isLookingForClosingSeparator) {
              if (openingSeparator.test(element.innerHTML)) {
                pairs.push(element.outerHTML);
                isLookingForClosingSeparator = true;
                currentPairNodeSeparationCount = 0;
              }
            } else {
              if (
                closingSeparator.test(element.innerHTML) &&
                currentPairNodeSeparationCount === 1
              ) {
                pairs.push(element.outerHTML);
                tags.push(pairs);
              }

              // Reset the pairs
              pairs = [];
              isLookingForClosingSeparator = false;
            }
          } else {
            if (isLookingForClosingSeparator && currentPairNodeSeparationCount >= 1) {
              // Reset the pairs
              pairs = [];
              isLookingForClosingSeparator = false;
            }
          }
        }

        if (node.childNodes) {
          for (const childNode of node.childNodes) {
            if (childNode.nodeType === Node.ELEMENT_NODE) {
              const childElement = childNode as HTMLElement;
              tags = [
                ...tags,
                ...findTagsBrokenIntoMultipleLinesWithOpeningAndClosingSeparators(
                  childElement.outerHTML,
                  findingTag,
                  openingSeparator,
                  closingSeparator,
                ),
              ];
            }
          }
        }
      }
    }
  }

  return tags;
};

export const fixTagsBrokenIntoMultipleLinesWithOpeningAndClosingSeparators = (
  htmlString: string,
  findingTag: string,
  openingSeparator: RegExp,
  closingSeparator: RegExp,
  openingFixFunction: (content: string) => string,
  closingFixFunction: (content: string) => string,
): string => {
  if (!htmlString) {
    return '';
  }

  let parsedHTML = htmlString;

  if (_hasDocumentFragments()) {
    const fragment = document.createRange()?.createContextualFragment(htmlString);
    const childNodes = fragment.childNodes;
    /**
     * Node arrays do not inherit from Array.prototype, convert to a normal
     * Array for easier handling
     */
    const nodesArray = childNodes ? Array.from(childNodes) : [];
    let pairs: HTMLElement[] = [];
    let isLookingForClosingSeparator = false;
    let currentPairNodeSeparationCount = 0;

    if (Array.isArray(nodesArray) && nodesArray.length) {
      for (const node of nodesArray) {
        currentPairNodeSeparationCount++;

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          if (element.nodeName === findingTag) {
            // Opening and closing separators should be siblings for the tag to be bolded
            if (!isLookingForClosingSeparator) {
              if (openingSeparator.test(element.innerHTML)) {
                pairs.push(element);
                isLookingForClosingSeparator = true;
                currentPairNodeSeparationCount = 0;
              }
            } else {
              if (
                closingSeparator.test(element.innerHTML) &&
                currentPairNodeSeparationCount === 1
              ) {
                // Update the pair so that they are enclosed between <b> tags
                const initialNode = pairs[0];
                const closingNode = element;
                initialNode.innerHTML = openingFixFunction(initialNode.innerHTML);
                closingNode.innerHTML = closingFixFunction(closingNode.innerHTML);
              }

              // Reset the pairs
              pairs = [];
              isLookingForClosingSeparator = false;
            }
          } else {
            if (isLookingForClosingSeparator && currentPairNodeSeparationCount >= 1) {
              // Reset the pairs
              pairs = [];
              isLookingForClosingSeparator = false;
            }
          }
        }

        if (node.childNodes) {
          for (const childNode of node.childNodes) {
            if (childNode.nodeType === Node.ELEMENT_NODE) {
              const childElement = childNode as HTMLElement;
              const fixedChildHTML = fixTagsBrokenIntoMultipleLinesWithOpeningAndClosingSeparators(
                childElement.outerHTML,
                findingTag,
                openingSeparator,
                closingSeparator,
                openingFixFunction,
                closingFixFunction,
              );

              childElement.outerHTML = fixedChildHTML;
            }
          }
        }
      }
    }

    parsedHTML = _getHTMLStringFromDocumentFragment(fragment);
  }

  return parsedHTML;
};

/**
 * Removes any auto-generated text from job skills (knowledge disciplines,
 * machines and brands)
 * @param {String} description - a job description
 */
export const removeJobSkillsTextFromJobDescription = (description: string): string => {
  const autGeneratedTextTitles = [
    'You should be proficient in:',
    `Machines & technologies you'll use:`,
  ];

  let newDescription = description;
  if (
    typeof document?.createRange === 'function' &&
    typeof document.createRange()?.createContextualFragment === 'function'
  ) {
    const fragment = document.createRange()?.createContextualFragment(description);

    const titleNodes = fragment.querySelectorAll('p');
    const titleNodesArray = titleNodes ? Array.from(titleNodes) : [];

    if (Array.isArray(titleNodesArray) && titleNodesArray.length) {
      titleNodes.forEach((titleNode) => {
        if (
          autGeneratedTextTitles.includes(`${titleNode.textContent}`.trim()) &&
          titleNode.nextElementSibling?.nodeName === 'UL'
        ) {
          titleNode.parentNode?.removeChild(titleNode.nextElementSibling);
          titleNode.parentNode?.removeChild(titleNode);
        }
      });
    }
    /**
     * Serialize through a detached container so root-level text nodes survive.
     * Mapping `outerHTML` over the child nodes dropped text nodes entirely
     * (text nodes have no `outerHTML`), which destroyed div-heavy or
     * text-plus-<br> ATS descriptions - ONE-5724.
     */
    newDescription = _getHTMLStringFromDocumentFragment(fragment);
  }
  return newDescription;
};

export const makeMarkdownVersionOfJobDescription = (htmlDescription: string): string => {
  let md = showdownConverter.makeMarkdown(htmlDescription);
  // Remove unwanted hr's where **** => <hr>
  md = md.replace(/\*\*\*\*/g, '');

  return md;
};

/**
 * Removes any auto-generated text from the job description and also any div
 * tag that might cause problems - ch[21712]
 * @param {String} htmlDescription - an html job description
 */
export const cleanHTMLJobDescription = (htmlDescription: string): string => {
  const descriptionWithoutDivs = removeTagsFromHTMLString(htmlDescription, 'div');
  const descriptionWithoutCarriageReturns = descriptionWithoutDivs.replace(/\n/g, '');
  const descriptionWithoutExplicitCarriageReturns = descriptionWithoutCarriageReturns.replace(
    /\\n/g,
    '',
  );
  const descriptionWithoutHrs = removeTagsFromHTMLString(
    descriptionWithoutExplicitCarriageReturns,
    'hr',
  );

  const descriptionWithoutBoldAsterisksNotBeingFormatted =
    JobDescriptionUtils.fixBoldAsterisksNotBeingFormatted(descriptionWithoutHrs);

  const descriptionWithoutBoldAsterisksInAnotherLine =
    JobDescriptionUtils.fixBoldAsterisksInAnotherLine(
      descriptionWithoutBoldAsterisksNotBeingFormatted,
    );

  const descriptionWithoutBulletPointsInAnotherLine =
    JobDescriptionUtils.fixBulletPointsInAnotherLine(descriptionWithoutBoldAsterisksInAnotherLine);

  return removeJobSkillsTextFromJobDescription(descriptionWithoutBulletPointsInAnotherLine);
};

/**
 * returns a MD text of job skills related to a job
 */
export const getJobSkillsTextForJobDescription = ({
  knowledgeDisciplines,
  machinesBrands,
}: {
  knowledgeDisciplines: string[];
  machinesBrands: { title: string; brands: string[] }[];
}): string => {
  let autoGeneratedText = '';
  if (knowledgeDisciplines.length) {
    autoGeneratedText += `<!-- -->\n\nYou should be proficient in:\n`;
    for (const knowledgeDiscipline of knowledgeDisciplines) {
      autoGeneratedText += `- ${knowledgeDiscipline}\n`;
    }
    autoGeneratedText += `\n`;
  }

  if (machinesBrands.length) {
    autoGeneratedText += `<!-- -->\n\nMachines & technologies you'll use:\n`;
    for (const machineBrands of machinesBrands) {
      let technologyMachineText = `- ${machineBrands.title}`;
      // add any related brands
      const brands = machineBrands.brands;
      if (Array.isArray(brands) && brands.length) {
        const brandTitles = brands.join(', ');
        technologyMachineText += ` (${brandTitles})\n`;
      } else {
        technologyMachineText += '\n';
      }

      autoGeneratedText += technologyMachineText;
    }
    autoGeneratedText += `\n`;
  }

  return autoGeneratedText;
};

// Removes '<' and '>' symbols from links in the job description, because that
// results in empty links
export const formatJobDescription = (jobDescription: string): string =>
  jobDescription
    .replace(/\[[^\]]+\]\([^)]+\)/g, (match) => match.replace(/]\(</, '](').replace(/>\)$/, ')'))
    .replace(new RegExp(String.fromCharCode(160), 'g'), '\n');

/**
 * UTILS
 */

/**
 * @description Can we create new document fragments?
 */
const _hasDocumentFragments = (): boolean => {
  return (
    typeof document?.createRange === 'function' &&
    typeof document.createRange()?.createContextualFragment === 'function'
  );
};

/**
 * @description Traverses all node's and it's children and removes the specified
 * tag from the nodes
 * @param nodesArray
 * @param removingTagName
 * @param removesInnerContent
 */
const _traverseAndRemoveTagsFromNodeArray = (
  nodesArray: Node[],
  removingTagName: string,
  removesInnerContent: boolean,
): void => {
  /**
   * While there are still nodes to check, traverse de node tree and remove
   * the specified tags.
   */
  while (nodesArray.length > 0) {
    // Get the first node in order
    const node = nodesArray.shift();
    const parentNode = node?.parentNode;

    if (node?.nodeName === removingTagName && parentNode) {
      // If we are not keeping it's content, just remove the node entirely
      if (removesInnerContent) {
        parentNode.removeChild(node);
      } else {
        _exchangeNodeWithChildrenNodes(node, nodesArray, parentNode);
      }
    }
  }
};

/**
 * @description Removes the given node and exchanges it's place on it's parent
 * node with all of its children nodes.
 * @param node
 * @param nodesArray
 * @param parentNode
 */
const _exchangeNodeWithChildrenNodes = (node: Node, nodesArray: Node[], parentNode: Node): void => {
  /**
   * Traverse the children of this node and expand them as children
   * of the parent
   */
  const childrenNodes = Array.from(node.childNodes);

  for (const childNode of childrenNodes) {
    nodesArray.push(childNode);
  }

  if (!childrenNodes.length) {
    // just remove the node as it doesn't has any children
    parentNode.removeChild(node);
  } else {
    /**
     * replace the node with the last child node, this removes the
     * node from the parent. Then appendBefore that last node the
     * following last child until we insert all the child nodes.
     * There only exists a insertBefore function and not a insertAfter
     * function
     */
    let lastNodeInserted = childrenNodes[childrenNodes.length - 1];
    parentNode.replaceChild(lastNodeInserted, node);

    for (let i = childrenNodes.length - 2; i >= 0; i--) {
      const lastNodeInsertedParentNode = lastNodeInserted.parentNode;
      if (lastNodeInsertedParentNode) {
        lastNodeInsertedParentNode.insertBefore(childrenNodes[i], lastNodeInserted);
      }
      lastNodeInserted = childrenNodes[i];
    }
  }
};

/**
 * Serializes a DocumentFragment via a detached container's `innerHTML` so text
 * nodes keep their HTML-entity escaping (`&lt;` stays `&lt;` instead of
 * decoding to a literal `<`) and comment nodes stay comments. Appending the
 * fragment to the container empties it, so callers must not reuse the fragment.
 */
const _getHTMLStringFromDocumentFragment = (fragment: DocumentFragment): string => {
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
};
